import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { businessTimeZone } from './date'

dayjs.extend(utc)
dayjs.extend(timezone)

// Frontend mirror of the backend deriveAffiliateFundsStatus
// (vlg-api app/services/payments/fundsAvailability.js).
//
// An ACH/e-check payment reaching 'cleared' means the client's bank settled, NOT that the
// money is safe to pay the affiliate — an NSF return can still arrive after settlement. The
// backend stamps transactionData.availableOn = settlement + N business days; the affiliate
// view shows a hold until that date instead of a plain "Cleared".
//
// Derived, never stored — availableOn is the only durable anchor. A cleared row with no
// availableOn (legacy, or a non-ACH payment) reads as available, i.e. today's behaviour.
export const deriveAffiliateFundsStatus = ({ statusValue, availableOn, now = new Date() }) => {
  if (statusValue === 'failed') return 'reversed'
  if (statusValue === 'processing' || statusValue === 'scheduled') return 'pending'
  if (statusValue === 'cleared') {
    if (!availableOn) return 'available'
    return dayjs(now).valueOf() >= dayjs.utc(availableOn).valueOf() ? 'available' : 'clearing'
  }
  return 'pending'
}

// Human label + the badge palette for each derived state. Only 'clearing' differs from the
// normal status badge — everything else falls through to the payment's own status styling.
//
// Shown to EVERYONE, not only affiliates. Mendy, 4 Sep 2026: "you are placing fake clears into
// the system ... just add a payment status 'processing' or 'in-transit' until it fully clears or
// nsf, instead of putting it as paid/clear." A settled e-check can still be returned for
// uncollected funds, so "Cleared" before the availability date claims money that is not there.
//
// Reads "In Transit" rather than "Clearing": that is the wording Mendy and Dolly settled on, and
// it is what affiliates are being told in reports.
export const fundsHoldLabel = ({ statusValue, availableOn, now = new Date() }) => {
  const derived = deriveAffiliateFundsStatus({ statusValue, availableOn, now })
  if (derived !== 'clearing') return null // caller keeps the existing status badge
  const date = dayjs.utc(availableOn).tz(businessTimeZone).format('MMM D')
  return {
    derived,
    label: `In Transit · ${date}`,
    // amber "in progress" treatment, distinct from cleared (green) and failed (red)
    bgColor: 'var(--color-warning-600)',
    textColor: '#fff',
  }
}

// Kept so any caller still importing the old name keeps working; the behaviour is identical.
export const affiliateFundsLabel = fundsHoldLabel
